'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import {
  Pencil,
  Check,
  X,
  ExternalLink,
  Copy,
  AlertCircle
} from 'lucide-react';
import { SettingsSidebar } from '@/components/dashboard/settings/SettingsSidebar';

interface EditFieldModal {
  field: string;
  label: string;
  value: string;
  type?: 'text' | 'email' | 'url' | 'select';
  options?: string[];
  description?: string;
}

export default function BusinessSettingsPage() {
  const [name, setName] = useState('Juan Rojas');
  const [legalName, setLegalName] = useState('Santangelo S.R.L.');
  const [category, setCategory] = useState('Barbería & Peluquería');
  const [address, setAddress] = useState('Urquiza 1623, Rosario');
  const [googleMapsUrl, setGoogleMapsUrl] = useState('https://maps.app.goo.gl/Rosario');
  const [phone, setPhone] = useState('+54 9 3462 554433');
  const [email, setEmail] = useState('juandario.rojas2@gmail.com');
  const [timezone, setTimezone] = useState('America/Argentina/Buenos_Aires');
  const [slug, setSlug] = useState('juanrojas');

  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editModal, setEditModal] = useState<EditFieldModal | null>(null);
  const [modalInputValue, setModalInputValue] = useState('');
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const fetchBusinessData = useCallback(async () => {
    try {
      const res = await fetch('/api/business');
      const data = await res.json();
      if (data.success && data.business) {
        const b = data.business;
        if (b.name) setName(b.name);
        if (b.legalName) setLegalName(b.legalName);
        if (b.category) setCategory(b.category);
        if (b.address) setAddress(b.address);
        if (b.googleMapsUrl) setGoogleMapsUrl(b.googleMapsUrl);
        if (b.phone) setPhone(b.phone);
        if (b.email) setEmail(b.email);
        if (b.timezone) setTimezone(b.timezone);
        if (b.slug) setSlug(b.slug);
      }
    } catch (e) {
      console.warn('Error syncing business data:', e);
    }
  }, []);

  useEffect(() => {
    fetchBusinessData();
  }, [fetchBusinessData]);

  const handleOpenEdit = (field: string, label: string, currentValue: string, type: 'text' | 'email' | 'url' | 'select' = 'text', options?: string[], description?: string) => {
    setEditModal({ field, label, value: currentValue, type, options, description });
    setModalInputValue(currentValue);
  };

  const handleSaveModal = async () => {
    if (!editModal) return;
    setSaving(true);
    setStatusMessage(null);

    try {
      const payload: any = {
        name,
        legalName,
        category,
        address,
        googleMapsUrl,
        phone,
        email,
        timezone,
        slug
      };

      payload[editModal.field] = modalInputValue;

      const res = await fetch('/api/business', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();

      if (data.success) {
        // Update local state
        if (editModal.field === 'name') setName(modalInputValue);
        if (editModal.field === 'legalName') setLegalName(modalInputValue);
        if (editModal.field === 'category') setCategory(modalInputValue);
        if (editModal.field === 'address') setAddress(modalInputValue);
        if (editModal.field === 'googleMapsUrl') setGoogleMapsUrl(modalInputValue);
        if (editModal.field === 'phone') setPhone(modalInputValue);
        if (editModal.field === 'email') setEmail(modalInputValue);
        if (editModal.field === 'slug') setSlug(modalInputValue);

        setEditModal(null);
        setStatusMessage({ text: 'Cambios guardados exitosamente.', type: 'success' });
        setTimeout(() => setStatusMessage(null), 3500);
      } else {
        setStatusMessage({ text: data.error || 'Error al guardar el campo.', type: 'error' });
      }
    } catch (err: any) {
      setStatusMessage({ text: err.message || 'Error de conexión', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = () => {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://dev.calenddy.com';
    const publicUrl = `${baseUrl}/${slug}`;
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-20 font-[family-name:var(--font-plus-jakarta)]">
      {statusMessage && (
        <div
          className={`p-4 rounded-xl text-xs font-semibold flex items-center justify-between shadow-2xs ${
            statusMessage.type === 'success'
              ? 'bg-[#E6F6F4] text-[#005C52] border border-[#00A896]/30'
              : 'bg-rose-50 text-rose-800 border border-rose-200'
          }`}
        >
          <span>{statusMessage.text}</span>
          <button
            onClick={() => setStatusMessage(null)}
            className="text-gray-400 hover:text-gray-700 font-bold ml-4"
          >
            ✕
          </button>
        </div>
      )}

      {/* Grid: 2 Columnas (Sub-sidebar a la izquierda, Tabla a la derecha) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Columna Izquierda: Sub-sidebar de Configuración */}
        <div className="lg:col-span-4">
          <SettingsSidebar currentPath="/dashboard/settings" />
        </div>

        {/* Columna Derecha: Vista Negocio idéntica a Calendico */}
        <div className="lg:col-span-8 space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
              Negocio
            </h2>
            <p className="text-sm text-gray-500 font-normal mt-1">
              Cómo se llama y dónde queda. Se usa en la página, en los mensajes y en los comprobantes.
            </p>
          </div>

          {/* Tabla de Datos del Negocio (Idéntica a Calendico) */}
          <div className="bg-white border border-gray-200/90 rounded-2xl overflow-hidden shadow-2xs divide-y divide-gray-100">
            {/* Fila: Nombre */}
            <div
              onClick={() => handleOpenEdit('name', 'Nombre', name, 'text', undefined, 'Nombre visible para clientes en el portal y mensajes.')}
              className="py-4 px-6 flex items-center justify-between hover:bg-gray-50/60 transition-colors cursor-pointer group"
            >
              <div className="w-1/3 text-sm text-gray-500 font-normal">
                Nombre
              </div>
              <div className="flex-1 text-sm text-gray-900 font-semibold truncate pr-4">
                {name || <span className="text-gray-400 italic font-normal">Sin definir</span>}
              </div>
              <button
                type="button"
                className="text-gray-400 group-hover:text-[#00A896] transition-colors p-1"
                title="Editar nombre"
              >
                <Pencil className="w-4 h-4" />
              </button>
            </div>

            {/* Fila: Razón social */}
            <div
              onClick={() => handleOpenEdit('legalName', 'Razón social', legalName, 'text', undefined, 'Nombre fiscal registrado para comprobantes y facturación.')}
              className="py-4 px-6 flex items-center justify-between hover:bg-gray-50/60 transition-colors cursor-pointer group"
            >
              <div className="w-1/3 text-sm text-gray-500 font-normal">
                Razón social
              </div>
              <div className="flex-1 text-sm text-gray-900 font-semibold truncate pr-4">
                {legalName || <span className="text-gray-400 italic font-normal">Ej: Santangelo S.R.L.</span>}
              </div>
              <button
                type="button"
                className="text-gray-400 group-hover:text-[#00A896] transition-colors p-1"
                title="Editar razón social"
              >
                <Pencil className="w-4 h-4" />
              </button>
            </div>

            {/* Fila: Rubro */}
            <div
              onClick={() => handleOpenEdit('category', 'Rubro comercial', category, 'select', ['Barbería & Peluquería', 'Estética & Belleza', 'Salud & Masajes', 'Tatuajes & Piercing', 'Otro servicio'], 'Categoría principal de tu actividad.')}
              className="py-4 px-6 flex items-center justify-between hover:bg-gray-50/60 transition-colors cursor-pointer group"
            >
              <div className="w-1/3 text-sm text-gray-500 font-normal">
                Rubro
              </div>
              <div className="flex-1 text-sm text-gray-900 font-semibold truncate pr-4">
                {category}
              </div>
              <button
                type="button"
                className="text-gray-400 group-hover:text-[#00A896] transition-colors p-1"
                title="Editar rubro"
              >
                <Pencil className="w-4 h-4" />
              </button>
            </div>

            {/* Fila: Dirección */}
            <div
              onClick={() => handleOpenEdit('address', 'Dirección', address, 'text', undefined, 'Calle, número y ciudad donde atiendes.')}
              className="py-4 px-6 flex items-center justify-between hover:bg-gray-50/60 transition-colors cursor-pointer group"
            >
              <div className="w-1/3 text-sm text-gray-500 font-normal">
                Dirección
              </div>
              <div className="flex-1 text-sm text-gray-900 font-semibold truncate pr-4">
                {address || <span className="text-gray-400 italic font-normal">Ej: Urquiza 1623, Rosario</span>}
              </div>
              <button
                type="button"
                className="text-gray-400 group-hover:text-[#00A896] transition-colors p-1"
                title="Editar dirección"
              >
                <Pencil className="w-4 h-4" />
              </button>
            </div>

            {/* Fila: Link de Google Maps */}
            <div
              onClick={() => handleOpenEdit('googleMapsUrl', 'Link de Google Maps', googleMapsUrl, 'url', undefined, 'Enlace para que tus clientes puedan abrir el GPS.')}
              className="py-4 px-6 flex items-center justify-between hover:bg-gray-50/60 transition-colors cursor-pointer group"
            >
              <div className="w-1/3 text-sm text-gray-500 font-normal">
                Link de Google Maps
              </div>
              <div className="flex-1 text-sm text-gray-900 font-semibold truncate pr-4 font-mono text-xs text-[#005C52]">
                {googleMapsUrl || <span className="text-gray-400 italic font-normal">Sin enlace cargado</span>}
              </div>
              <button
                type="button"
                className="text-gray-400 group-hover:text-[#00A896] transition-colors p-1"
                title="Editar enlace Maps"
              >
                <Pencil className="w-4 h-4" />
              </button>
            </div>

            {/* Fila: Teléfono */}
            <div
              onClick={() => handleOpenEdit('phone', 'Teléfono', phone, 'text', undefined, 'Teléfono de contacto o atención al cliente.')}
              className="py-4 px-6 flex items-center justify-between hover:bg-gray-50/60 transition-colors cursor-pointer group"
            >
              <div className="w-1/3 text-sm text-gray-500 font-normal">
                Teléfono
              </div>
              <div className="flex-1 text-sm text-gray-900 font-semibold truncate pr-4">
                {phone || <span className="text-gray-400 italic font-normal">Sin teléfono</span>}
              </div>
              <button
                type="button"
                className="text-gray-400 group-hover:text-[#00A896] transition-colors p-1"
                title="Editar teléfono"
              >
                <Pencil className="w-4 h-4" />
              </button>
            </div>

            {/* Fila: Correo del negocio */}
            <div
              onClick={() => handleOpenEdit('email', 'Correo del negocio', email, 'email', undefined, 'Email para avisos del sistema y consultas de clientes.')}
              className="py-4 px-6 flex items-center justify-between hover:bg-gray-50/60 transition-colors cursor-pointer group"
            >
              <div className="w-1/3 text-sm text-gray-500 font-normal">
                Correo del negocio
              </div>
              <div className="flex-1 text-sm text-gray-900 font-semibold truncate pr-4">
                {email || <span className="text-gray-400 italic font-normal">tu@negocio.com</span>}
              </div>
              <button
                type="button"
                className="text-gray-400 group-hover:text-[#00A896] transition-colors p-1"
                title="Editar correo"
              >
                <Pencil className="w-4 h-4" />
              </button>
            </div>

            {/* Fila: Link público del negocio */}
            <div
              onClick={() => handleOpenEdit('slug', 'Dirección web pública (Slug)', slug, 'text', undefined, 'Identificador de la URL para que tus clientes reserven.')}
              className="py-4 px-6 flex items-center justify-between hover:bg-gray-50/60 transition-colors cursor-pointer group"
            >
              <div className="w-1/3 text-sm text-gray-500 font-normal">
                Página pública
              </div>
              <div className="flex-1 text-sm text-[#00A896] font-semibold truncate pr-4 flex items-center gap-2">
                <span>dev.calenddy.com/{slug}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyLink();
                  }}
                  className="p-1 text-gray-400 hover:text-gray-700"
                  title="Copiar link"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-[#00A896]" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
              <button
                type="button"
                className="text-gray-400 group-hover:text-[#00A896] transition-colors p-1"
                title="Editar enlace público"
              >
                <Pencil className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Quirúrgico de Edición de Campo (Estilo Calendico) */}
      {editModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-xl border border-gray-200 overflow-hidden animate-fade-in">
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">
                  Editar {editModal.label}
                </h3>
                <button
                  type="button"
                  onClick={() => setEditModal(null)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {editModal.description && (
                <p className="text-xs text-gray-500">
                  {editModal.description}
                </p>
              )}

              <div>
                {editModal.type === 'select' ? (
                  <select
                    value={modalInputValue}
                    onChange={(e) => setModalInputValue(e.target.value)}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm font-medium text-gray-900 focus:bg-white focus:ring-2 focus:ring-[#00A896]/20 focus:border-[#00A896] transition"
                  >
                    {editModal.options?.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={editModal.type || 'text'}
                    value={modalInputValue}
                    onChange={(e) => setModalInputValue(e.target.value)}
                    autoFocus
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm font-medium text-gray-900 focus:bg-white focus:ring-2 focus:ring-[#00A896]/20 focus:border-[#00A896] transition"
                  />
                )}
              </div>
            </div>

            <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={() => setEditModal(null)}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-200 transition cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={handleSaveModal}
                className="px-5 py-2 rounded-xl bg-[#00A896] hover:bg-[#009181] active:bg-[#00776B] disabled:opacity-50 text-white text-sm font-semibold shadow-sm transition flex items-center gap-1.5 cursor-pointer"
              >
                {saving ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
